const DEFAULT_RETURN_TO = 'https://onboarding.risescience.com';

/** sessionStorage key for the captured returnTo. sessionStorage (not
 *  localStorage) so it's scoped to this funnel run / tab — it survives SPA
 *  navigation + refreshes but won't leak a stale link into a future,
 *  unrelated visit. */
export const RETURN_TO_STORAGE_KEY = 'osa_return_to';

/** A returnTo is only honored if it's a valid absolute http(s) URL —
 *  it's an open-redirect surface, so anything else is rejected. */
const isValidReturnTo = (value: string | null | undefined): value is string => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const readStoredReturnTo = (): string | null => {
  try {
    const stored = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    return isValidReturnTo(stored) ? stored : null;
  } catch {
    // sessionStorage may throw under privacy modes; fall through.
    return null;
  }
};

/**
 * Captures the funnel-entry returnTo into sessionStorage. Called once on
 * funnel entry (OsaRoutes mount — child route changes don't remount it, so
 * this runs per fresh entry, not per in-funnel navigation).
 *
 *   - Valid ?returnTo= present → store it (survives a later dropped query).
 *   - No/invalid param → clear any stored value, so a fresh run that wasn't
 *     given a returnTo can't inherit a stale link from a prior run in the
 *     same tab (sessionStorage outlives a single funnel run).
 */
export const captureReturnToFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  const raw = new URLSearchParams(window.location.search).get('returnTo');
  try {
    if (isValidReturnTo(raw)) {
      window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, raw);
    } else {
      window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may throw under privacy modes — the URL param remains
    // the primary source, so this is non-fatal.
  }
};

/**
 * Resolves where to send the user when they exit the funnel. Priority:
 *   1. `?returnTo=` on the current URL (highest — always wins)
 *   2. sessionStorage value captured on entry (survives dropped queries)
 *   3. the production onboarding entry (default)
 *
 * Only http(s) URLs are honored at every layer; anything else falls
 * through. This funnel is linked from the onboarding survey, so the
 * default sends users back there; an explicit returnTo (e.g. a local
 * instance, ?returnTo=http://localhost:3333/start) overrides it.
 */
export const readReturnToFromUrl = (): string => {
  if (typeof window === 'undefined') return DEFAULT_RETURN_TO;
  const fromUrl = new URLSearchParams(window.location.search).get('returnTo');
  if (isValidReturnTo(fromUrl)) return fromUrl;
  return readStoredReturnTo() ?? DEFAULT_RETURN_TO;
};
